import { type PortalMessage, resolvePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The Organization pages' shared renderer — design §27.15 ("each console gets one shared page renderer … so every
 * page below is data plus real API calls, never a bespoke layout"). Written by plan-014 unit B2, the first unit to
 * land an Organization page; C2 (Security), B5 (Sharing) and B4 (Archive and close) draw theirs with the same
 * blocks.
 *
 * Drawn to the prototype's Organization block renderer (`design/WeTalk.dc.html`, `data-screen-label="Organization"`):
 * title, lede, the tab strip, the outcome notice (the prototype's toast), then blocks — `stats`, `list`,
 * `table`, `form`, `notice` — each with an optional head (title, dek, buttons). Styles are `./organization-page.css`,
 * tokens only (ADR 0018).
 *
 * AMENDED 2026-10-09 (design sync, Claude Design version 1791495439483263): the kicker above the title is gone
 * from every Organization page; the tab strip gains "Workspaces" after Overview (the design dropped its Workspaces
 * screen — the list lives here now); the cards take the console's card finish (`--wt-card-r`, `--wt-card-sh`).
 * A person in a workspace with no organization role that reads the organization sees the Workspaces tab alone —
 * the workspaces they belong to ("everyone else sees only the ones they belong to").
 */
import { useQuery } from "@tanstack/react-query";
import { Icon, type PhosphorIcon } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import type { FormEvent, ReactNode } from "react";

import { useGateway } from "../../../api/provider.js";
import { authContextQuery } from "../../../api/query.js";
import { GLYPH } from "../../../shell/glyph.js";

import "./organization-page.css";

/** The Organization tab strip, in the prototype's order (copy paths: `/organization/…`). */
export const ORGANIZATION_TAB = [
  { id: "overview", get label() { return portalText("core", "account_admin.overview_0efc2e"); }, to: "/organization" },
  { id: "workspaces", get label() { return portalText("core", "account_admin.workspaces_205b45"); }, to: "/organization/workspaces" },
  { id: "people", get label() { return portalText("core", "account_admin.people_and_roles_df661e"); }, to: "/organization/people" },
  { id: "security", get label() { return portalText("core", "account_admin.security_f25ce1"); }, to: "/organization/security" },
  { id: "sharing", get label() { return portalText("core", "account_admin.sharing_78779b"); }, to: "/organization/sharing" },
  { id: "archive-close", get label() { return portalText("core", "account_admin.archive_and_close_6377a8"); }, to: "/organization/archive-close" },
] as const;
export type OrganizationTabId = (typeof ORGANIZATION_TAB)[number]["id"];

/** The outcome of the last act on the page — the prototype's toast, with real words. */
export interface Outcome {
  readonly tone: "done" | "problem";
  readonly text: PortalMessage;
}

/** Whether the open context's organization role reads the organization (every tab), or only Workspaces is open. */
function useReadsOrganization(): boolean {
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway)).data;
  if (context === undefined || context === null || context.kind === "selection_required") return true;
  return context.capability.account.includes("account.profile.read");
}

export function OrganizationPage(props: {
  readonly tab: OrganizationTabId;
  readonly title: string;
  readonly lede: string;
  readonly outcome?: Outcome | null;
  readonly children?: ReactNode;
}): ReactNode {
  usePortalTranslation("core");
  const readsOrganization = useReadsOrganization();
  const shown = readsOrganization ? ORGANIZATION_TAB : ORGANIZATION_TAB.filter((one) => one.id === "workspaces");
  return (
    <section className="op-page">
      <h1 className="op-title">{props.title}</h1>
      <p className="op-lede">{props.lede}</p>
      <nav className="op-tabs" aria-label={portalText("core", "account_admin.organization_519255")}>
        {shown.map((one) => (
          <Link key={one.id} to={one.to} className={one.id === props.tab ? "op-tab op-tab-on" : "op-tab"} aria-current={one.id === props.tab ? "page" : undefined}>
            {one.label}
          </Link>
        ))}
      </nav>
      {props.outcome === undefined || props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
      {props.children}
    </section>
  );
}

/** One block, with its optional head (title, dek and the buttons on the right). */
export function Block(props: {
  readonly title?: string;
  readonly dek?: string;
  readonly buttons?: ReactNode;
  readonly children?: ReactNode;
  readonly label?: string;
}): ReactNode {
  usePortalTranslation("core");
  const hasHead = props.title !== undefined || props.buttons !== undefined;
  return (
    <div className="op-block" aria-label={props.label}>
      {hasHead ? (
        <div className="op-block-head">
          <div className="op-block-heading">
            {props.title === undefined ? null : <p className="op-block-title">{props.title}</p>}
            {props.dek === undefined ? null : <p className="op-block-dek">{props.dek}</p>}
          </div>
          {props.buttons === undefined ? null : <div className="op-block-buttons">{props.buttons}</div>}
        </div>
      ) : null}
      {props.children}
    </div>
  );
}

/** The `stats` block. */
export function StatGrid(props: { readonly stat: readonly { readonly label: string; readonly value: string; readonly note: string }[] }): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-stats">
      {props.stat.map((one) => (
        <div key={one.label} className="op-stat">
          <span className="op-stat-label">{one.label}</span>
          <span className="op-stat-value">{one.value}</span>
          <span className="op-stat-note">{one.note}</span>
        </div>
      ))}
    </div>
  );
}

/** The prototype's three tints: `a` (accent), `n` (neutral) and `b` (accent-2, for what needs care). */
export type Tone = "accent" | "neutral" | "caution";

const TAG_CLASS: Readonly<Record<Tone, string>> = { accent: "tag-accent", neutral: "tag-neutral", caution: "tag-accent-2" };

/** One row of a `list` block. */
export function ItemRow(props: {
  readonly glyph: PhosphorIcon;
  readonly tone: Tone;
  readonly title: string;
  readonly tag: string | null;
  readonly dek: string;
  readonly meta?: string;
  readonly actions?: ReactNode;
}): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-item">
      <span className={`op-item-icon op-tone-${props.tone}`}>
        <Icon glyph={props.glyph} size="lg" />
      </span>
      <span className="op-item-body">
        <span className="op-item-head">
          <span className="op-item-title">{props.title}</span>
          {props.tag === null ? null : <span className={`tag ${TAG_CLASS[props.tone]} op-item-tag`}>{props.tag}</span>}
        </span>
        <span className="op-item-dek">{props.dek}</span>
      </span>
      <span className="op-item-side">
        {props.meta === undefined ? null : <span className="op-item-meta">{props.meta}</span>}
        {props.actions}
      </span>
    </div>
  );
}

/** The `list` block's column. */
export function ItemColumn(props: { readonly children?: ReactNode; readonly label?: string }): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-list" aria-label={props.label}>
      {props.children}
    </div>
  );
}

/** The `notice` block. */
export function Notice(props: {
  readonly tone: Tone;
  readonly glyph: PhosphorIcon;
  readonly title: string;
  readonly text: string;
  readonly actions?: ReactNode;
}): ReactNode {
  usePortalTranslation("core");
  return (
    <div className={`op-notice op-notice-${props.tone}`}>
      <Icon glyph={props.glyph} size="lg" />
      <span className="op-notice-body">
        <span className="op-notice-title">{props.title}</span>
        <span className="op-notice-text">{props.text}</span>
      </span>
      {props.actions === undefined ? null : <span className="op-notice-actions">{props.actions}</span>}
    </div>
  );
}

/** The `form` block's panel: a grid of fields on the surface colour. */
export function FormPanel(props: { readonly children?: ReactNode; readonly onSubmit?: (event: FormEvent<HTMLFormElement>) => void; readonly label?: string }): ReactNode {
  usePortalTranslation("core");
  return (
    <form className="op-form" onSubmit={props.onSubmit} aria-label={props.label} noValidate>
      {props.children}
    </form>
  );
}

/** A plain-words line for an empty, loading or failed block. */
export function BlockNote(props: { readonly children: ReactNode }): ReactNode {
  usePortalTranslation("core");
  return (
    <p className="c-note op-note" role="status">
      {props.children}
    </p>
  );
}
